import { fmtINR } from '../../lib/currency';
import { useCallback, useEffect, useState } from 'react';
import {
  amazongrowthApi,
  type ActivityEntry,
  type BusinessImpact,
  type CompetitiveInsight,
  type ExecutionRecord,
  type GrowthDashboard,
  type GrowthOpportunity,
  type GrowthRecommendation,
  type GrowthScores,
  type SeasonalInsight,
} from '../../api/amazongrowth';

type Tab = 'overview' | 'opportunities' | 'recommendations' | 'execution' | 'impact';


function typeLabel(t: string) {
  return t.replace(/_/g, ' ');
}

export function AmazonGrowthPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<GrowthDashboard | null>(null);
  const [opportunities, setOpportunities] = useState<GrowthOpportunity[]>([]);
  const [recommendations, setRecommendations] = useState<GrowthRecommendation[]>([]);
  const [executions, setExecutions] = useState<ExecutionRecord[]>([]);
  const [scores, setScores] = useState<GrowthScores | null>(null);
  const [impact, setImpact] = useState<BusinessImpact | null>(null);
  const [seasonal, setSeasonal] = useState<SeasonalInsight[]>([]);
  const [competitive, setCompetitive] = useState<CompetitiveInsight[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, opps, recs, execs, sc, imp, sea, comp, act] = await Promise.all([
        amazongrowthApi.dashboard(),
        amazongrowthApi.listOpportunities(),
        amazongrowthApi.listRecommendations(),
        amazongrowthApi.listExecutions(),
        amazongrowthApi.scores(),
        amazongrowthApi.businessImpact(),
        amazongrowthApi.seasonal(),
        amazongrowthApi.competitive(),
        amazongrowthApi.listActivity(),
      ]);
      setDashboard(dash);
      setOpportunities(opps);
      setRecommendations(recs);
      setExecutions(execs);
      setScores(sc);
      setImpact(imp);
      setSeasonal(sea);
      setCompetitive(comp);
      setActivity(act);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load growth platform');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const approveOpp = async (id: string) => {
    setBusy(true);
    try {
      await amazongrowthApi.approveOpportunity(id, 'growth-manager');
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const startExec = async (recId: string) => {
    setBusy(true);
    try {
      await amazongrowthApi.startExecution(recId);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Execution failed — approval may be required');
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Growth Overview' },
    { id: 'opportunities', label: 'Opportunity Center' },
    { id: 'recommendations', label: 'Recommendation Workspace' },
    { id: 'execution', label: 'Execution Monitor' },
    { id: 'impact', label: 'Impact Tracker' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Amazon Growth Intelligence</h1>
          <p className="text-sm text-slate-500">
            AI-powered growth consultant — opportunities, recommendations &amp; governed execution — BCP-00001
          </p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-orange-100 text-orange-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && scores && impact && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Growth Score', value: `${dashboard.growth_score.toFixed(1)}` },
              { label: 'Open Opportunities', value: dashboard.opportunities_open },
              { label: 'Priority Opportunities', value: dashboard.priority_opportunities },
              { label: 'Revenue Potential', value: fmtINR(dashboard.revenue_potential) },
              { label: 'Revenue at Risk', value: fmtINR(dashboard.revenue_at_risk) },
              { label: 'Active Recommendations', value: dashboard.active_recommendations },
              { label: 'Executions Running', value: dashboard.executions_running },
              { label: 'Realized Impact', value: fmtINR(dashboard.realized_impact) },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-900">Growth Scores</h2>
              <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                {[
                  ['Organization', scores.organization_score],
                  ['Brand', scores.brand_score],
                  ['Category', scores.category_score],
                  ['Product', scores.product_score],
                  ['Opportunity', scores.opportunity_score],
                  ['Execution Readiness', scores.execution_readiness_score],
                ].map(([label, val]) => (
                  <div key={String(label)} className="flex justify-between rounded-lg bg-slate-50 px-3 py-2">
                    <dt className="text-slate-600">{label}</dt>
                    <dd className="font-medium">{Number(val).toFixed(1)}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-900">Seasonal Intelligence</h2>
              <ul className="mt-3 space-y-2">
                {seasonal.map((s) => (
                  <li key={s.id} className="rounded-lg border border-slate-100 px-3 py-2 text-sm">
                    <p className="font-medium">{s.title}</p>
                    <p className="text-xs text-slate-500">Readiness {s.readiness_pct}% · {s.days_until ? `${s.days_until} days` : 'ongoing'}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {competitive.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-900">Competitive Intelligence</h2>
              <ul className="mt-3 space-y-2 text-sm">
                {competitive.map((c) => (
                  <li key={c.id} className="flex justify-between rounded-lg border border-slate-100 px-3 py-2">
                    <span>{c.insight}</span>
                    <span className="text-xs text-slate-500">{(c.score * 100).toFixed(0)}% confidence</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {tab === 'opportunities' && (
        <div className="space-y-3">
          {opportunities.map((o) => (
            <div key={o.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">#{o.priority_rank}</span>
                    <span className="text-xs uppercase text-slate-400">{typeLabel(o.type)}</span>
                    <span className="text-xs text-slate-500">{o.status}</span>
                  </div>
                  <p className="mt-1 font-medium text-slate-900">{o.title}</p>
                  <p className="mt-1 text-sm text-slate-600">{o.business_problem}</p>
                  {o.evidence && <p className="mt-1 text-xs text-slate-500">Evidence: {o.evidence}</p>}
                </div>
                <div className="text-right text-sm">
                  <p className="font-semibold text-emerald-700">{fmtINR(o.expected_revenue_impact)}</p>
                  <p className="text-xs text-slate-500">{(o.confidence_score * 100).toFixed(0)}% confidence</p>
                  {o.requires_approval && o.status !== 'approved' && o.status !== 'executing' && (
                    <button type="button" onClick={() => approveOpp(o.id)} disabled={busy}
                      className="mt-2 rounded-lg bg-orange-600 px-3 py-1 text-xs font-medium text-white hover:bg-orange-700 disabled:opacity-50">
                      Approve
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-3">
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900">{r.title}</p>
                  <p className="mt-1 text-sm text-slate-600">{r.recommendation}</p>
                  <p className="mt-2 text-xs text-slate-500">Effort: {r.estimated_effort}</p>
                  {r.dependencies && r.dependencies.length > 0 && (
                    <p className="mt-1 text-xs text-slate-400">Deps: {r.dependencies.join(', ')}</p>
                  )}
                </div>
                <div className="text-right text-sm shrink-0">
                  <p className="font-semibold">{fmtINR(r.expected_revenue_impact)}</p>
                  <p className="text-xs text-slate-500">Profit {fmtINR(r.expected_profit_impact)}</p>
                  <button type="button" onClick={() => startExec(r.id)} disabled={busy}
                    className="mt-2 rounded-lg border border-orange-300 px-3 py-1 text-xs font-medium text-orange-700 hover:bg-orange-50 disabled:opacity-50">
                    Execute
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'execution' && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Copilot</th>
                <th className="px-4 py-3">Workflow</th>
                <th className="px-4 py-3">Publishing</th>
                <th className="px-4 py-3">Governance</th>
              </tr>
            </thead>
            <tbody>
              {executions.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No executions yet</td></tr>
              ) : executions.map((e) => (
                <tr key={e.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 font-medium">{e.title}</td>
                  <td className="px-4 py-3">{e.status}</td>
                  <td className="px-4 py-3 text-xs">{e.copilot_session_id ?? '—'}</td>
                  <td className="px-4 py-3 text-xs">{e.workflow_id ?? '—'}</td>
                  <td className="px-4 py-3 text-xs">{e.publishing_job_id ?? '—'}</td>
                  <td className="px-4 py-3">{e.governance_approved ? 'Approved' : 'Pending'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'impact' && impact && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: 'Revenue at Risk', value: fmtINR(impact.revenue_at_risk) },
              { label: 'Revenue Potential', value: fmtINR(impact.revenue_potential) },
              { label: 'Realized Revenue', value: fmtINR(impact.realized_revenue) },
              { label: 'Realized Profit', value: fmtINR(impact.realized_profit) },
              { label: 'Completed Actions', value: impact.completed_actions },
              { label: 'Pending Actions', value: impact.pending_actions },
              { label: 'Avg Confidence', value: `${(impact.avg_confidence * 100).toFixed(0)}%` },
            ].map((k) => (
              <div key={k.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs text-slate-500">{k.label}</p>
                <p className="mt-1 text-xl font-semibold">{k.value}</p>
              </div>
            ))}
          </div>
          {activity.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold">Recent Activity</h2>
              <ul className="mt-2 space-y-1 text-sm text-slate-600">
                {activity.slice(0, 8).map((a) => <li key={a.id}>{a.message}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
